/**
 * pSEO API client (Phase 1, 2026-06-07).
 *
 * Typed fetch wrappers around GET /api/pseo/active-slugs and
 * GET /api/pseo/slugs/{slug}. Called from:
 *   - web/app/sitemap.ts (lists all active slugs every build)
 *   - web/app/c/[slug]/page.tsx (resolves DB-backed slugs on visit)
 *
 * Server-side only. getInternalAuthHeaders returns empty on the
 * browser so the secret never leaks; calls happen in RSC / Route
 * Handlers.
 */

import { getInternalAuthHeaders } from "./internal-headers";

function apiBaseUrl(): string {
  if (typeof window === "undefined") {
    return (
      process.env.API_INTERNAL_URL ??
      process.env.API_URL ??
      "http://localhost:8000"
    );
  }
  return process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
}

export interface ActiveSlugWire {
  slug: string;
  country_code: string;
  /** ISO-8601 timestamp string the API returns. */
  last_refresh_at: string;
  /**
   * Phase 3 (2026-06-08): page kind so the sitemap builds the right
   * URL (/c/<slug> for product, /c/list/<slug> for list). Defaults to
   * "product" if the API doesn't include the field (pre-Phase-3 rows).
   */
  page_kind?: "product" | "list" | "comparison";
}

export interface ActiveSlugsResponse {
  items: ActiveSlugWire[];
}

export interface ParsedIntentWire {
  raw_query: string;
  product_label: string;
  brand: string | null;
  model_number: string | null;
  category: string;
  specs: Record<string, string>;
  constraints: string[];
  urgency: string;
  confidence: number;
  summary: string;
  out_of_scope_category?: string | null;
  out_of_scope_reason?: string | null;
  /**
   * Phase 3 (2026-06-08): list-page editorial paragraph, stashed in
   * parsed_intent JSONB by upsert_list_slug. Generated once per slug
   * at birth via LLM, preserved across refreshes. Absent on product
   * pages and on list pages where the LLM call failed; the renderer
   * handles null gracefully.
   */
  editorial_paragraph?: string | null;
}

export interface SlugDetailResponse {
  slug: string;
  page_kind: "product" | "list" | "comparison";
  country_code: string;
  raw_query: string;
  parsed_intent: ParsedIntentWire;
  retailer_count: number;
  /**
   * 2026-09-04: whether search engines should index this page (>=3
   * retailers; branded "best-<model>" list mirrors excluded). Drives the
   * robots meta; the sitemap applies the same policy server-side.
   * Optional so web-ahead-of-api deploy skew degrades to "index".
   */
  index_eligible?: boolean;
  status: string;
  last_refresh_at: string;
  /**
   * 2026-06-22: the canonical slug for this slug's search. Equal to
   * `slug` when this IS the canonical; a different slug when this is a
   * non-deterministic-parse duplicate or a retired slug whose search
   * still has an active page. The /c route permanent-redirects when it
   * differs from the requested slug. Optional so a brief web-ahead-of-api
   * deploy skew degrades to "no redirect" rather than a type error.
   */
  canonical_slug?: string;
}

/**
 * Sitemap-build fetch timeout. Vercel/Next.js build phase blocks on
 * sitemap generation, so an unresponsive API must not stall the
 * deploy. 10 seconds is comfortably above any healthy /api/pseo
 * response time (single indexed query, expected <200ms) and well
 * inside the Vercel build wall-clock.
 */
const SITEMAP_FETCH_TIMEOUT_MS = 10_000;

/**
 * Fetch every active /c/<slug> for the country.
 *
 * Returns an empty list when the API is unreachable OR slow so the
 * sitemap generation never crashes or hangs the build. Logs to
 * console so prod build logs surface the failure.
 */
export async function listActiveSlugs(
  countryCode: string = "AU",
  // 2026-09-04: indexOnly=true is the SITEMAP view - only pages passing the
  // API's indexing policy (>=3 retailers, no branded list mirrors). All other
  // callers (internal links) omit it and see every active page, unchanged.
  opts: { indexOnly?: boolean } = {},
): Promise<ActiveSlugWire[]> {
  const url =
    `${apiBaseUrl()}/api/pseo/active-slugs?country_code=${encodeURIComponent(countryCode)}` +
    (opts.indexOnly ? "&index_only=true" : "");
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    SITEMAP_FETCH_TIMEOUT_MS,
  );
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { ...getInternalAuthHeaders() },
      cache: "no-store",
      signal: controller.signal,
    });
    if (!response.ok) {
      console.warn(
        `[pseo] GET /api/pseo/active-slugs returned ${response.status}; sitemap will include hardcoded entries only`,
      );
      return [];
    }
    const body = (await response.json()) as ActiveSlugsResponse;
    return body.items ?? [];
  } catch (e) {
    const isTimeout = (e as Error).name === "AbortError";
    console.warn(
      `[pseo] GET /api/pseo/active-slugs ${
        isTimeout ? "timed out" : "network error"
      }: ${(e as Error).message}; sitemap will include hardcoded entries only`,
    );
    return [];
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Fetch one slug's stored ParsedIntent. Returns null on 404 or
 * network error so the route handler can fall through to its
 * hardcoded slug map without crashing.
 */
/**
 * 2026-09-05 (owner-caught dead-end): a RETIRED page is not a wall.
 * The API hands back the page's own query so the route can bounce the
 * visitor into a fresh search - which, on success, resurrects the
 * page via the publisher's resurrection rule.
 */
export type RetiredSlug = { retiredQuery: string };

export function isRetiredSlug(
  d: SlugDetailResponse | RetiredSlug | null,
): d is RetiredSlug {
  return !!d && typeof (d as RetiredSlug).retiredQuery === "string";
}

export async function getSlugDetail(
  slug: string,
): Promise<SlugDetailResponse | RetiredSlug | null> {
  const url = `${apiBaseUrl()}/api/pseo/slugs/${encodeURIComponent(slug)}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { ...getInternalAuthHeaders() },
      cache: "no-store",
    });
    if (response.status === 404) {
      try {
        const body = (await response.json()) as {
          detail?: { error?: string; raw_query?: string };
        };
        if (
          body?.detail?.error === "page_retired" &&
          body.detail.raw_query
        ) {
          return { retiredQuery: body.detail.raw_query };
        }
      } catch {
        // fall through to the plain miss
      }
      return null;
    }
    if (!response.ok) {
      console.warn(
        `[pseo] GET /api/pseo/slugs/${slug} returned ${response.status}`,
      );
      return null;
    }
    return (await response.json()) as SlugDetailResponse;
  } catch (e) {
    console.warn(
      `[pseo] GET /api/pseo/slugs/${slug} network error: ${(e as Error).message}`,
    );
    return null;
  }
}


export interface ResolveResponse {
  slug: string | null;
  page_kind: "product" | "list" | "comparison" | null;
}

/**
 * Stage 4 (2026-06-28): resolve a raw query to a saved /c/<slug> page, if Mio
 * already has one. /compare calls this FIRST so a repeat search serves the
 * saved page instantly and consistently instead of re-running a live search
 * (which races a ~60s budget and can land on 0 retailers on a slow run).
 * Returns null on a miss / any error so the caller falls through to the normal
 * clarify/search flow.
 */
export async function resolveQuery(
  q: string,
  cc = "AU",
): Promise<ResolveResponse | null> {
  const url = `${apiBaseUrl()}/api/pseo/resolve?q=${encodeURIComponent(q)}&cc=${encodeURIComponent(cc)}`;
  // 2026-09-10: one retry. This bounce is fail-open (a miss falls through
  // to a PAID live search), and a transient blip - e.g. an api pod mid-
  // deploy - used to convert directly into spend. Evidence: six paid
  // "Beats Studio Pro" searches during a morning of rolling deploys while
  // the register held the answer the whole time. The stream endpoint now
  // carries its own server-side guard too; this retry just makes the
  // cheap first hop sturdier.
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: { ...getInternalAuthHeaders() },
        cache: "no-store",
      });
      if (response.ok) {
        const data = (await response.json()) as ResolveResponse;
        return data.slug ? data : null;
      }
    } catch {
      // fall through to the retry / final null
    }
  }
  return null;
}


/**
 * pSEO SWR (2026-06-11): cache-only read of a comparison page.
 *
 * The new endpoint serves the cached SearchResult instantly
 * (<1 second TTFB) without running a live search. When the cached
 * row is older than 24 hours, the backend fires a background Inngest
 * refresh; the current request still returns the stale row.
 *
 * Returns null on 404 (slug missing OR no cache row yet) or network
 * error so the page can render UnreachableState instead of crashing.
 */
export interface ComparisonResponseWire {
  slug: string;
  page_kind: "product" | "list" | "comparison";
  country_code: string;
  // SearchResult shape from the backend; the consumer projects only
  // the fields it renders (groups, success_count, tier_groups, etc.)
  // and tolerates new fields landing on the wire.
  result: {
    groups: Array<{
      canonical_name?: string;
      canonical_brand?: string | null;
      canonical_gtin?: string | null;
      image_url?: string | null;
      products: unknown[];
      [k: string]: unknown;
    }>;
    fetched_at: string;
    cache_hit?: boolean;
    cache_key?: string | null;
    target_count?: number;
    success_count?: number;
    failure_count?: number;
    tier_groups?: Record<string, unknown[]>;
    unreachable?: unknown[];
    [k: string]: unknown;
  };
  fetched_at: string;
  is_stale: boolean;
  stale_threshold_hours: number;
  /**
   * Option C (2026-07-31): true when the row is past the hard staleness
   * ceiling AND the ceiling is enabled. When true the /c page shows the
   * normal searching scene and loads fresh prices for the visitor instead
   * of serving the old cache. Optional so a web-ahead-of-api deploy skew
   * degrades to "not very stale" rather than a type error.
   */
  is_very_stale?: boolean;
  hard_stale_ceiling_hours?: number;
  /**
   * 2026-09-04 Price Check strip: precomputed "is this a good price?"
   * summary. Null/absent (flag off, old row, thin data) renders nothing.
   */
  price_check?: Record<string, unknown> | null;
  /**
   * 2026-09-19 one best-price rule: the winner the API picked for this page
   * (app/services/headline_offer.py) and the site-wide layout switch
   * (MIO_ONE_CARD_ENABLED on the api). The page renders THIS offer first;
   * the watch card, alert email, chips and price check read the same pick.
   */
  headline?: HeadlineWire | null;
  layout?: "one_card" | "classic";
}

export interface HeadlineWire {
  retailer_slug: string | null;
  product_url: string | null;
  delivered_total: string | null;
  stock_status: string | null;
  layout: string;
}

/**
 * B-201 (2026-09-12): ask the API to refresh a stale /c page in the
 * background. Signed-in only - the endpoint is Clerk-gated, which is the
 * owner's locked rule that only a signed-in person may trigger paid work.
 *
 * Why this exists: `get_comparison` stops enqueueing the refresh itself
 * when `mio_live_prices_signed_in_only` is on, and points at this
 * endpoint instead - but nothing ever called it, so pages never got
 * refreshed from a visit. The page then re-ran a full foreground search
 * on every single visit and never saved the result.
 *
 * Fire-and-forget. The API dedupes per slug, so calling it on every
 * stale page load is safe and collapses to one refresh.
 */
export async function requestStaleRefresh(
  slug: string,
  token: string,
  countryCode = "AU",
): Promise<void> {
  try {
    await fetch(`${apiBaseUrl()}/api/pseo/refresh`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ slug, country_code: countryCode }),
      keepalive: true,
    });
  } catch {
    /* best-effort: a refresh request must never affect the page */
  }
}

export async function getComparisonBySlug(
  slug: string,
): Promise<ComparisonResponseWire | null> {
  const url = `${apiBaseUrl()}/api/pseo/comparison/${encodeURIComponent(slug)}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { ...getInternalAuthHeaders() },
      cache: "no-store",
    });
    if (response.status === 404) return null;
    if (!response.ok) {
      console.warn(
        `[pseo] GET /api/pseo/comparison/${slug} returned ${response.status}`,
      );
      return null;
    }
    return (await response.json()) as ComparisonResponseWire;
  } catch (e) {
    console.warn(
      `[pseo] GET /api/pseo/comparison/${slug} network error: ${(e as Error).message}`,
    );
    return null;
  }
}

/**
 * Browse pages (2026-06-23): the internal-linking crawl path. /c pages are
 * orphans (nothing links to them) which is the dominant reason Google
 * leaves them "Discovered, currently not indexed". /browse + /browse/<cat>
 * give Google (and users) a path to every page.
 *
 * NOTE: these use `next: { revalidate }` (ISR-cacheable), NOT `cache:
 * "no-store"` like the /c read path above. The browse pages are pure
 * directory content that changes slowly, so they should be statically
 * cached and served from the edge, not re-rendered per request.
 */

const BROWSE_REVALIDATE_S = 43_200; // 12h, matches the comparison TTL

export interface BrowseCategoryWire {
  category: string;
  count: number;
  /**
   * Top product titles in the category, for the hub-card teaser. Optional so
   * a brief web-ahead-of-api deploy skew degrades to "no teaser" rather than
   * a crash.
   */
  teaser_titles?: string[];
}

export interface BrowseSlugWire {
  slug: string;
  page_kind: "product" | "list" | "comparison";
  title: string;
  retailer_count: number;
  /**
   * Best-effort cheapest True Final Price (AUD, e.g. "1147.00"), read from
   * the comparison cache. null when the cache misses; the card then shows no
   * price rather than a fabricated one.
   */
  cheapest_price?: string | null;
}

export interface BrowseCategoryResponse {
  category: string;
  page: number;
  page_size: number;
  total: number;
  items: BrowseSlugWire[];
}

/** Fetch the categories that have published pages (the /browse hub). */
export async function getBrowseCategories(
  countryCode: string = "AU",
): Promise<BrowseCategoryWire[]> {
  const url = `${apiBaseUrl()}/api/pseo/browse?country_code=${encodeURIComponent(countryCode)}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { ...getInternalAuthHeaders() },
      // no-store, NOT next:{revalidate}: a build-time cached fetch baked an
      // empty "No comparisons" snapshot into the prerendered /browse hub and
      // it survived a forced rebuild. Render at request time from the live
      // api, exactly like the /c read path (getComparisonBySlug). The hub
      // page is force-dynamic so it is never prerendered at build.
      cache: "no-store",
    });
    if (!response.ok) {
      console.warn(`[pseo] GET /api/pseo/browse returned ${response.status}`);
      return [];
    }
    const body = (await response.json()) as { items?: BrowseCategoryWire[] };
    return body.items ?? [];
  } catch (e) {
    console.warn(`[pseo] GET /api/pseo/browse error: ${(e as Error).message}`);
    return [];
  }
}

/**
 * Fetch one category's paginated pages. Returns null on 404 (unknown
 * category, outside the fixed taxonomy) so the route can notFound().
 */
export async function getBrowseCategory(
  category: string,
  page: number = 1,
  countryCode: string = "AU",
): Promise<BrowseCategoryResponse | null> {
  const url =
    `${apiBaseUrl()}/api/pseo/browse/${encodeURIComponent(category)}` +
    `?page=${page}&country_code=${encodeURIComponent(countryCode)}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { ...getInternalAuthHeaders() },
      next: { revalidate: BROWSE_REVALIDATE_S },
    });
    if (response.status === 404) return null;
    if (!response.ok) {
      console.warn(
        `[pseo] GET /api/pseo/browse/${category} returned ${response.status}`,
      );
      return null;
    }
    return (await response.json()) as BrowseCategoryResponse;
  } catch (e) {
    console.warn(
      `[pseo] GET /api/pseo/browse/${category} error: ${(e as Error).message}`,
    );
    return null;
  }
}
